import React, { memo, useMemo, useState } from "react";

import ChoiceCard from "../components/ChoiceCard.jsx";
import ChoiceFeatModal from "../components/ChoiceFeatModal.jsx";
import LoadingOrEmpty from "../components/LoadingOrEmpty.jsx";
import SearchBar from "../components/SearchBar.jsx";
import {
  ALL_SKILLS,
  getCardImage,
} from "../characterCreatorUtils.js";

const RaceCard = memo(function RaceCard({ race, selected, onSelect }) {
  const isCustomLineage = race.name === "Linhagem Customizada";
  return (
    <ChoiceCard
      selected={selected}
      imageUrl={getCardImage("raca", race)}
      imageLabel={`Raça ${race.name}`}
      imageVertical={true}
      badge={
        race.homebrew ? <span className="badge-hw">Homebrew</span> :
        isCustomLineage ? <span className="badge-custom">Custom</span> :
        null
      }
      onClick={() => onSelect(race)}
    >
      <div className="card-name">{race.name}</div>
      {race.traits?.length ? (
        <div className="card-traits">
          {race.traits.slice(0, 2).map((trait) => (
            <span key={trait} className="trait-tag" title={trait}>
              {trait.length > 22 ? trait.slice(0, 21) + "…" : trait}
            </span>
          ))}
          {race.traits.length > 2 && (
            <span className="trait-tag trait-tag--more">+{race.traits.length - 2}</span>
          )}
        </div>
      ) : null}
    </ChoiceCard>
  );
});

function HumanChoicePanel({ choice, onOpenModal }) {
  const selectedSkill = choice?.skill || "";
  const selectedFeat = choice?.feat || "";
  const isComplete = Boolean(selectedSkill && selectedFeat);

  return (
    <div className="side-detail-section">
      <div className="side-detail-section-title">Escolhas do Humano</div>

      <button type="button" className="cl-modal-trigger" onClick={onOpenModal}>
        {isComplete ? "Ver ou editar escolhas" : "Selecionar perícia e Talento de Origem"}
      </button>

      <div className="cl-choice-list">
        <div className="cl-choice-line">
          <span className="cl-choice-label">Perícia:</span>
          <span className="cl-choice-value">{selectedSkill || "Não selecionada"}</span>
        </div>
        <div className="cl-choice-line">
          <span className="cl-choice-label">Talento de Origem:</span>
          <span className="cl-choice-value">{selectedFeat || "Não selecionado"}</span>
        </div>
      </div>

      <div className="cl-hint">
        {isComplete
          ? "Escolhas confirmadas. Você pode abrir novamente para editar."
          : "Finalize as duas escolhas para avançar para a próxima etapa."}
      </div>
    </div>
  );
}


// ── CUSTOM LINEAGE FORM ──────────────────────────────────────

function CustomLineageForm({ customLineage, onOpenModal }) {
  if (!customLineage) return null;
  const selectedSkill = customLineage.skills?.find(Boolean) || "";
  const selectedBenefit = customLineage.darkvision ? "Visão no Escuro 60ft" : (selectedSkill ? `Perícia: ${selectedSkill}` : "Não selecionado");
  const selectedFeat = customLineage.originFeat || "";
  const hasCompletedChoices = Boolean(selectedFeat && (customLineage.darkvision || selectedSkill));

  return (
    <div className="side-detail-section">
      <div className="side-detail-section-title">Escolhas da Linhagem</div>

      <button type="button" className="cl-modal-trigger" onClick={onOpenModal}>
        {hasCompletedChoices ? "Ver ou editar escolhas" : "Definir escolhas da linhagem"}
      </button>

      <div className="cl-choice-list">
        <div className="cl-choice-line">
          <span className="cl-choice-label">Nome</span>
          <span className="cl-choice-value">{customLineage.lineageName || "Não definido"}</span>
        </div>
        <div className="cl-choice-line">
          <span className="cl-choice-label">Tamanho</span>
          <span className="cl-choice-value">{customLineage.size || "Medium"}</span>
        </div>
        <div className="cl-choice-line">
          <span className="cl-choice-label">Benefício:</span>
          <span className="cl-choice-value">{selectedBenefit}</span>
        </div>
        <div className="cl-choice-line">
          <span className="cl-choice-label">Talento de Origem:</span>
          <span className="cl-choice-value">{selectedFeat || "Não selecionado"}</span>
        </div>
      </div>

      <div className="cl-hint">
        {hasCompletedChoices
          ? "Escolhas confirmadas. Você pode abrir novamente para editar."
          : "Finalize as escolhas obrigatórias para avançar."}
      </div>
    </div>
  );
}

// ── RACE DETAIL ──────────────────────────────────────────────

function RaceDetail({
  race,
  humanChoice,
  customLineage,
  onOpenHumanModal,
  onOpenCustomLineageModal,
}) {
  if (!race) return null;

  const isHuman = race.name === "Humano";
  const isCustomLineage = race.name === "Linhagem Customizada";
  const size = isCustomLineage ? (customLineage?.size || race.size || "Medium") : (race.size || "Medium");
  const speed = race.speed || "30 ft";
  const creatureType = race.creatureType || "Humanoid";

  return (
    <div className="side-detail-panel">
      <div className="side-detail-name">{race.name}</div>
      {race.homebrew && <span className="badge-hw" style={{ position: "static", marginBottom: "0.5rem", display: "inline-block" }}>Homebrew</span>}
      {race.name === "Linhagem Customizada" && <span className="badge-custom" style={{ position: "static", marginBottom: "0.5rem", display: "inline-block" }}>Custom</span>}
      {race.detail && <p className="side-detail-summary">{race.detail}</p>}

      <div className="side-detail-section">
        <div className="side-detail-row">
          <span className="side-detail-label">Creature Type</span>
          <span className="side-detail-value">{creatureType}</span>
        </div>
        <div className="side-detail-row">
          <span className="side-detail-label">Size</span>
          <span className="side-detail-value">{size}</span>
        </div>
        <div className="side-detail-row">
          <span className="side-detail-label">Speed</span>
          <span className="side-detail-value">{speed}</span>
        </div>
      </div>

      {race.traits?.length > 0 && (
        <div className="side-detail-section">
          <div className="side-detail-section-title">Special Traits</div>
          <ul className="side-detail-list">
            {race.traits.map((trait, i) => (
              <li key={i}>{trait}</li>
            ))}
          </ul>
        </div>
      )}

      {isHuman ? (
        <HumanChoicePanel
          choice={humanChoice}
          onOpenModal={onOpenHumanModal}
        />
      ) : null}

      {isCustomLineage ? (
        <CustomLineageForm
          customLineage={customLineage}
          onOpenModal={onOpenCustomLineageModal}
        />
      ) : null}
    </div>
  );
}

function RaceEmpty() {
  return (
    <div className="side-empty-panel">
      <div className="side-empty-icon">🧬</div>
      <div className="side-empty-text">
        Selecione uma raça para ver tipo de criatura, tamanho, velocidade e traços especiais.
      </div>
    </div>
  );
}

// ── MAIN STEP ────────────────────────────────────────────────

export default function RaceStep({
  state,
  races,
  originFeats,
  onSelectRace,
  onPatchCustomLineage,
  onPatchHumanRaceChoice,
}) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [choiceModalMode, setChoiceModalMode] = useState(null);

  const categories = useMemo(() => {
    if (!races) return [];
    return [...new Set(races.map((r) => r.category).filter((c) => c && c !== "Homebrew"))];
  }, [races]);

  const filtered = useMemo(() => {
    if (races === null) return null;
    let result = races;
    if (category) result = result.filter((r) => r.category === category);
    if (query) {
      const q = query.toLowerCase();
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.detail?.toLowerCase().includes(q) ||
          r.traits?.some((t) => t.toLowerCase().includes(q))
      );
    }
    return result;
  }, [races, query, category]);

  const selectedRace = state.race || null;
  const customLineageSkill = state.customLineage?.skills?.find(Boolean) || "";

  const modalInitialSelection = useMemo(() => {
    if (choiceModalMode === "human") {
      return {
        benefitType: "skill",
        skill: state.humanRaceChoice?.skill || "",
        feat: state.humanRaceChoice?.feat || "",
      };
    }

    if (choiceModalMode === "custom-lineage") {
      return {
        benefitType: state.customLineage?.darkvision ? "darkvision" : "skill",
        skill: customLineageSkill,
        feat: state.customLineage?.originFeat || "",
        lineageName: state.customLineage?.lineageName || "",
        size: state.customLineage?.size || "Medium",
      };
    }

    return null;
  }, [choiceModalMode, state.humanRaceChoice, state.customLineage, customLineageSkill]);

  const normalRaces = filtered?.filter(r => r.name !== "Linhagem Customizada") || [];
  const customLineageRace = filtered?.find(r => r.name === "Linhagem Customizada") || null;

  function handleSelectRace(race) {
    if (state.race?.name !== race.name) {
      onSelectRace(race);
    }
    setChoiceModalMode(null);
  }

  function handleConfirmModal(selection) {
    if (choiceModalMode === "human") {
      onPatchHumanRaceChoice?.({
        skill: selection.skill || "",
        feat: selection.feat || "",
      });
      setChoiceModalMode(null);
      return;
    }

    if (choiceModalMode === "custom-lineage") {
      const choseDarkvision = selection.benefitType === "darkvision";
      onPatchCustomLineage({
        lineageName: selection.lineageName || "",
        size: selection.size || "Medium",
        originFeat: selection.feat || "",
        darkvision: choseDarkvision,
        skills: choseDarkvision ? ["", ""] : [selection.skill || "", ""],
      });
      setChoiceModalMode(null);
    }
  }

  return (
    <div className="step-split-layout">
      <div className="step-cards-col">
        <SearchBar value={query} onChange={setQuery} placeholder="Buscar raça…" />
        {categories.length > 0 && (
          <div className="category-filter">
            <button
              type="button"
              className={`cat-btn${!category ? " cat-btn--active" : ""}`}
              onClick={() => setCategory("")}
            >
              Todos
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`cat-btn${category === cat ? " cat-btn--active" : ""}`}
                onClick={() => setCategory(category === cat ? "" : cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
        <div className="card-grid card-grid--portrait" id="allRaces">
          <LoadingOrEmpty
            items={filtered}
            loadingText="Carregando raças…"
            emptyText={query ? `Nenhuma raça encontrada para "${query}".` : "Nenhuma raça disponível."}
          >
            {normalRaces.map((race) => (
              <RaceCard
                key={race.name}
                race={race}
                selected={state.race?.name === race.name}
                onSelect={handleSelectRace}
              />
            ))}
            {customLineageRace && (
              <>
                <div className="custom-divider" style={{ gridColumn: "1 / -1" }}>
                  <span className="custom-divider-line" />
                  <span className="custom-divider-label">Opção Especial</span>
                  <span className="custom-divider-line" />
                </div>
                <RaceCard
                  race={customLineageRace}
                  selected={state.race?.name === customLineageRace.name}
                  onSelect={handleSelectRace}
                />
              </>
            )}
          </LoadingOrEmpty>
        </div>
      </div>

      <div className="step-info-col">
        {selectedRace ? (
          <RaceDetail
            race={selectedRace}
            humanChoice={state.humanRaceChoice}
            customLineage={state.customLineage}
            onOpenHumanModal={() => setChoiceModalMode("human")}
            onOpenCustomLineageModal={() => setChoiceModalMode("custom-lineage")}
          />
        ) : <RaceEmpty />}
      </div>

      <ChoiceFeatModal
        isOpen={Boolean(choiceModalMode)}
        mode={choiceModalMode || "human"}
        skills={ALL_SKILLS}
        feats={originFeats || []}
        initialSelection={modalInitialSelection}
        onClose={() => setChoiceModalMode(null)}
        onConfirm={handleConfirmModal}
      />
    </div>
  );
}
